import React from 'react';
import { motion } from 'framer-motion';
import { 
  CircleDollarSign, Target, Droplets, Users, 
  Lock, Eye, CheckCircle2 
} from 'lucide-react';

const goals = [
  {
    icon: CircleDollarSign,
    title: 'High Circulating at TGE',
    description: '80% of tokens available at launch for maximum market participation'
  },
  {
    icon: Droplets,
    title: 'Deep Launch Liquidity',
    description: '22% allocated to liquidity pools ensuring healthy price discovery'
  },
  {
    icon: Users,
    title: 'Ecosystem Activation',
    description: 'Immediate community rewards, grants, and validator programs'
  },
  {
    icon: Target,
    title: 'Long-term Alignment',
    description: 'Extended vesting for team and investors ensures commitment'
  },
  {
    icon: Eye,
    title: 'Transparent Structure',
    description: 'All allocations publicly viewable with on-chain verification'
  }
];

export const TokenOverviewSection = () => {
  return (
    <section id="overview" className="py-24 relative">
      {/* Background effects */}
      <div className="absolute inset-0 neural-grid opacity-30" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-primary text-sm font-medium tracking-wider uppercase">
            Token Overview
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mt-3 mb-4">
            Built for Sustainability & Growth
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            $NEURO tokenomics are designed to maximize long-term value while ensuring 
            fair distribution and deep liquidity from day one.
          </p>
        </motion.div>
        
        {/* Total Supply Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-8 md:p-12 text-center mb-12 border-glow max-w-2xl mx-auto"
        >
          <p className="text-muted-foreground mb-2">Total Supply</p>
          <div className="flex items-baseline justify-center gap-2">
            <span className="text-5xl md:text-6xl font-bold text-primary">1B</span>
            <span className="text-2xl text-muted-foreground">$NEURO</span>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            Fixed supply. No inflation. No discretionary minting.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <CheckCircle2 className="w-4 h-4 text-success" />
            <span className="text-sm text-success">100% Fixed Supply</span>
          </div>
        </motion.div>
        
        {/* Goals Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {goals.map((goal, index) => (
            <motion.div
              key={goal.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass rounded-xl p-6 group hover:border-primary/30 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <goal.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-foreground mb-2">
                {goal.title}
              </h3>
              <p className="text-sm text-muted-foreground">
                {goal.description}
              </p>
            </motion.div>
          ))}
        </div>
        
        {/* Neural line separator */}
        <div className="mt-24 neural-line" />
      </div>
    </section>
  );
};

export default TokenOverviewSection;
